import type { SuggestedQuestion } from "@scoach/types";
import { DEFAULT_CUSTOM_QUESTIONS } from "@scoach/types";
import { useEffect, useState } from "react";

import { authApi } from "../auth/api.ts";
import { useAuthStore } from "../auth/store.ts";

/**
 * Shared source for "repeated question" suggestions, used by the Settings banner,
 * the post-meeting summary nudge, and the dashboard nudge. Fetches once (module
 * cache) and keeps every mounted surface in sync; add/dismiss update the user's
 * settings and drop the item everywhere.
 */
let cache: SuggestedQuestion[] | null = null;
let inflight: Promise<SuggestedQuestion[]> | null = null;
const listeners = new Set<(s: SuggestedQuestion[]) => void>();

function publish(next: SuggestedQuestion[]) {
  cache = next;
  for (const l of listeners) l(next);
}

function load(): Promise<SuggestedQuestion[]> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = authApi
      .fetchSuggestedQuestions()
      .then((s) => {
        publish(s);
        return s;
      })
      .catch(() => {
        publish([]);
        return [];
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function useSuggestedQuestions() {
  const [suggestions, setSuggestions] = useState<SuggestedQuestion[]>(cache ?? []);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  useEffect(() => {
    const l = (s: SuggestedQuestion[]) => setSuggestions(s);
    listeners.add(l);
    void load();
    return () => {
      listeners.delete(l);
    };
  }, []);

  function drop(question: string) {
    publish((cache ?? []).filter((s) => s.question !== question));
  }

  async function add(question: string) {
    drop(question);
    if (!user) return;
    const current = user.settings.customQuestions ?? DEFAULT_CUSTOM_QUESTIONS;
    const next = [...current, question];
    setUser({ ...user, settings: { ...user.settings, customQuestions: next } });
    await authApi.updateSettings({ customQuestions: next }).catch(() => {});
  }

  async function dismiss(question: string) {
    drop(question);
    if (!user) return;
    const current = user.settings.dismissedQuestionSuggestions ?? [];
    const next = [...current, question];
    setUser({ ...user, settings: { ...user.settings, dismissedQuestionSuggestions: next } });
    await authApi.updateSettings({ dismissedQuestionSuggestions: next }).catch(() => {});
  }

  return { suggestions, add, dismiss };
}
